'use client';

import { useState, useRef, useEffect, useCallback } from 'react';

interface Section {
  id: string;
  title: string;
  level: number;
}

/**
 * Fetches Privacy Notice via our own /api/legal-doc proxy.
 * After the iframe loads, we read its headings to build a sticky sidebar.
 * Same-origin iframe means we can access contentDocument freely.
 */
export default function PrivacyNotice() {
  const [isLoading, setIsLoading] = useState(true);
  const [error] = useState<string | null>(null);
  const [sections, setSections] = useState<Section[]>([]);
  const [activeId, setActiveId] = useState<string>('');
  const iframeRef = useRef<HTMLIFrameElement>(null);

  /* ── Build sidebar from iframe headings after load ─────────────────── */
  const handleIframeLoad = useCallback(() => {
    setIsLoading(false);

    try {
      const iframe = iframeRef.current;
      const doc = iframe?.contentDocument;
      if (!doc || !iframe) return;

      // Adjust iframe height to fit content
      const adjustHeight = () => {
        if (iframe.contentWindow && iframe.contentWindow.document.body) {
          iframe.style.height = iframe.contentWindow.document.documentElement.scrollHeight + 'px';
        }
      };
      
      // Initial adjust
      adjustHeight();
      
      // Re-adjust if content changes (e.g. images load)
      setTimeout(adjustHeight, 500);
      
      // Hide scrollbar inside iframe body just in case
      doc.body.style.overflow = 'hidden';


      const headings = Array.from(doc.querySelectorAll('h1, h2, h3'));
      const extracted: Section[] = headings.map((el, i) => {
        const id = el.id || `section-${i}`;
        if (!el.id) el.id = id;
        return {
          id,
          title: el.textContent?.trim() || '',
          level: parseInt(el.tagName[1]),
        };
      }).filter(s => s.title.length > 0 && s.level > 1); // skip h1 (page title)

      setSections(extracted);
      if (extracted.length > 0) setActiveId(extracted[0].id);
    } catch {
      // cross-origin guard (shouldn't happen since we own the proxy)
    }
  }, []);

  // Failsafe & Poll: Ensure handleIframeLoad runs even if onLoad event is missed
  useEffect(() => {
    const checkIframe = () => {
      const iframe = iframeRef.current;
      if (iframe && iframe.contentDocument && iframe.contentDocument.readyState === 'complete') {
        const body = iframe.contentDocument.body;
        // Wait until it has actual content, not just about:blank
        if (body && body.innerHTML.length > 50) {
          handleIframeLoad();
          return true;
        }
      }
      return false;
    };

    if (checkIframe()) return;

    const interval = setInterval(() => {
      if (checkIframe()) {
        clearInterval(interval);
      }
    }, 500);

    const timer = setTimeout(() => {
      setIsLoading(false);
      clearInterval(interval);
    }, 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(timer);
    };
  }, [handleIframeLoad]);


  /* ── Intersection observer inside the iframe to highlight active ────── */
  useEffect(() => {
    if (sections.length === 0 || isLoading) return;

    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;

    const observer = new (iframeRef.current!.contentWindow as any).IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { threshold: 0.3 }
    );

    sections.forEach(s => {
      const el = doc.getElementById(s.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [sections, isLoading]);

  /* ── Scroll iframe to section when sidebar link clicked ─────────────── */
  const scrollTo = (id: string) => {
    const doc = iframeRef.current?.contentDocument;
    const el = doc?.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveId(id);
    }
  };

  /* ── Loading skeleton ──────────────────────────────────────────────── */
  const Skeleton = () => (
    <div className="flex flex-col lg:flex-row gap-12">
      <aside className="lg:w-64 shrink-0 hidden lg:block">
        <div className="space-y-2">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-9 bg-foreground/5 rounded-lg animate-pulse" />
          ))}
        </div>
      </aside>
      <main className="flex-1 max-w-3xl space-y-4">
        <div className="h-12 bg-foreground/5 rounded-lg animate-pulse w-2/3" />
        <div className="h-4 bg-foreground/5 rounded animate-pulse w-1/4" />
        <div className="mt-8 space-y-3">
          {[...Array(12)].map((_, i) => (
            <div key={i} className="h-4 bg-foreground/5 rounded animate-pulse" />
          ))}
        </div>
      </main>
    </div>
  );

  return (
    <div className="min-h-screen text-gray-300 font-sans selection:bg-teal-900 selection:text-white">
      <div className="container mx-auto px-4 py-12 md:py-20 lg:py-24">

        {/* ── Loading skeleton overlay ─────────────────────────────── */}
        {isLoading && (
          <div className="w-full">
            <Skeleton />
          </div>
        )}

        {/* ── Main layout: sidebar + content ───────────────────────── */}
        <div className={`flex flex-col lg:flex-row gap-12 relative transition-opacity duration-300 ${isLoading ? 'opacity-0 absolute' : 'opacity-100'}`}>

          {/* Sticky sidebar */}
          {sections.length > 0 && (
            <aside className="w-full lg:w-72 shrink-0">
              <div className="lg:sticky lg:top-32 space-y-2 max-h-[calc(100vh-10rem)] overflow-y-auto no-visible-scrollbar p-1">
                <p className="text-white font-semibold mb-4 pl-4 text-lg hidden lg:block">
                  Contents
                </p>
                <nav className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-4 lg:pb-0">
                  {sections.map(s => (
                    <button
                      key={s.id}
                      onClick={() => scrollTo(s.id)}
                      className={`whitespace-nowrap w-max lg:w-full text-left px-4 py-2.5 flex-shrink-0 rounded-lg text-sm transition-all duration-150 ${
                        s.level === 3 ? 'pl-8' : ''
                      } ${
                        activeId === s.id
                          ? 'bg-teal-900/40 text-teal-300 border border-teal-800/50 font-medium cursor-pointer'
                          : 'text-gray-400 hover:text-white hover:bg-white/5 cursor-pointer'
                      }`}
                    >
                      {s.title}
                    </button>
                  ))}
                </nav>
              </div>
            </aside>
          )}

          {/* iframe content */}
          <main className="flex-1 min-w-0 max-w-3xl">
            {error ? (
              <div className="rounded-lg border border-red-500/20 bg-red-500/5 p-6">
                <p className="text-red-400 text-sm">
                  Unable to load the Privacy Notice at this time. Please try again later.
                </p>
              </div>
            ) : (
              <iframe
                ref={iframeRef}
                src="/api/legal-doc?type=privacy-notice"
                className="w-full min-h-[800px] border-none"
                title="Privacy Notice"
                onLoad={handleIframeLoad}
                scrolling="no"
              />
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
